/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

"use client";

import { useCosmosComponentContext } from "@components/context";
import { CosmosButton } from "@components/elements";
import { IndustryFragment } from "@cosmos/hygraph-schema";
import { useIsLoggedIn } from "@cosmos/next-hooks";
import { useLocation } from "@epic-mw/app-context";
import { useLocale, useMessages } from "@epic-mw/i18n";
import { useRouter } from "next/navigation";
import { Dispatch, SetStateAction, useCallback } from "react";

interface Props {
	selectedIndustry?: IndustryFragment | null;
	setSubmit: Dispatch<SetStateAction<boolean>>;
	disabled: boolean;
	isLoading: boolean;
	showIndustry: boolean;
}

export const NewsletterSignupButton = ({
	selectedIndustry,
	showIndustry,
	setSubmit,
	disabled,
	isLoading,
}: Props): React.JSX.Element => {
	const isLoggedIn = useIsLoggedIn();

	const router = useRouter();
	const locale = useLocale();
	const location = useLocation();
	const messages = useMessages();
	const { LoadingComponent } = useCosmosComponentContext();
	const submitButtonLabel = messages["epic.cosmos.newsletter.subscribe"];
	const signInButtonLabel = messages["epic.cosmos.newsletter.signin"];
	const industryRequired = messages["epic.cosmos.newsletter.industry.required"] || "";

	const handleSubmit = useCallback(() => {
		if (isLoggedIn) {
			setSubmit(true);
		} else {
			location.search = "";
			location.hash = "";
			if (selectedIndustry?.salesForceId) {
				location.searchParams.set("subscribe", selectedIndustry?.salesForceId);
				location.hash = "newsletter-signup";
			}

			router.push(`/${locale}/auth?redirectUrl=${encodeURIComponent(location.toString())}`);
		}
	}, [isLoggedIn, router, locale, selectedIndustry, setSubmit, location]);

	if (isLoading && LoadingComponent) {
		return <LoadingComponent fill={false} />;
	}

	const label = isLoggedIn ? submitButtonLabel : signInButtonLabel;

	return (
		<CosmosButton
			disabled={disabled}
			title={disabled && showIndustry ? industryRequired : label}
			onClick={handleSubmit}
			variant="primary"
			label={label}
			size="lg"
		/>
	);
};
